import React, {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import classNames from 'classnames';
import { Modal } from 'ncoded-component-library';
import {
  ModalProps,
  ModalRef,
} from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import useCallbackRef from 'hooks/useCallbackRef';
import ViewAndDownloadFileStatsTable from './components/ViewAndDownloadFileStatsTable';
import {
  SORTING_VALUES,
  ViewAndDownloadFileStatsTableData,
} from './components/ViewAndDownloadFileStatsTable/data';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import usePageParams from 'hooks/usePageParams';
import { Params } from 'react-router-dom';
import useQueryParams from 'hooks/useQueryParams';
import { FIRST_PAGE } from 'data/global';
import { DataRoomNode } from 'router/subrouters/DataRoom/models/DataRoomNode';
import MymeTypeIcon from 'router/subrouters/DataRoom/components/DataRoomDocumentsRouter/components/MymeTypeIcon';
import { getNodeMimeType, getUserFullName } from 'utils';
import { isMaximumSelected } from 'router/subrouters/DataRoom/utils';
import useTableFetchState from 'hooks/useTableFetchState';

import './ViewAndDownloadFileStatsModal.styles.scss';

const LIMIT = '10';
const PAGE_PARAM = 'viewAndDownloadPage';

type ViewAndDownloadFileStatsModalProps = {
  className?: string;
  dataRoomNode: DataRoomNode;
} & ModalProps;

const ViewAndDownloadFileStatsModal: React.ForwardRefRenderFunction<
  ModalRef,
  ViewAndDownloadFileStatsModalProps
> = (props, ref) => {
  const { className, dataRoomNode, ...rest } = props;

  const {
    isLoading,
    totalPages,
    totalItems,
    data,
    setIsLoading,
    setPaginatedData,
    reset,
  } = useTableFetchState<ViewAndDownloadFileStatsTableData>();

  const [sort, setSort] = useState(SORTING_VALUES.numberOfViews);

  const { popServerError } = useContext(PopNotificationsContext);

  const { setQueryParam } = useQueryParams<Params>();

  const [modal, modalRef] = useCallbackRef<ModalRef>();

  useImperativeHandle(ref, () => modal, [modal]);

  const classes = classNames('view-and-download-file-stats-modal', className);

  const page = usePageParams(PAGE_PARAM);

  const onClose = useCallback(() => {
    reset();
    setSort(SORTING_VALUES.numberOfViews);
    setQueryParam(PAGE_PARAM, FIRST_PAGE);
  }, [setQueryParam, reset]);

  useEffect(() => {
    if (!modal?.openState || !dataRoomNode?._id) return;

    (async () => {
      try {
        setIsLoading(true);
        const { data: response } = await dataRoomApi.reports.getReportsForNode(
          dataRoomNode?._id,
          {
            $sort: sort,
            $page: page,
            $limit: LIMIT,
          },
        );

        setPaginatedData({
          ...response,
          items: response.items.map(
            ({ dataRoomMember, numberOfDownloads, numberOfViews }) => ({
              numberOfDownloads,
              numberOfViews,
              dataRoomMemberName: getUserFullName(
                dataRoomMember?.firstName,
                dataRoomMember?.lastName,
              ),
            }),
          ),
        });
      } catch (e) {
        popServerError(e);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [
    dataRoomNode,
    modal?.openState,
    page,
    sort,
    popServerError,
    setPaginatedData,
    setIsLoading,
  ]);

  return (
    <Modal
      ref={modalRef}
      className={classes}
      onClose={onClose}
      title={
        <div className="file-name">
          <MymeTypeIcon
            mimeType={getNodeMimeType(dataRoomNode)}
            isLocked={isMaximumSelected(dataRoomNode?.fileSecurity)}
          />
          <span>
            <span>{dataRoomNode?.document?.name}</span>
          </span>
        </div>
      }
      {...rest}
    >
      <ViewAndDownloadFileStatsTable
        isLoading={isLoading}
        data={data}
        totalPages={totalPages}
        totalItems={totalItems}
        numberOfPlaceholderRows={+LIMIT}
        pageParam={PAGE_PARAM}
        sort={sort}
        updateSort={setSort}
      />
    </Modal>
  );
};

export default forwardRef(ViewAndDownloadFileStatsModal);
